import React, { useState, useEffect, useMemo } from 'react';
import { Property } from '../../types';
import { XMarkIcon, SpinnerIcon, SaveIcon, PlusIcon } from '../Icons';
import { useBrokerFilterOptions } from '../../hooks/useBrokerFilterOptions';
import { fetchInmobiliariasConfigApi } from '../../services/configApiClient';
import { CreatePropertyBasicFields } from './create_property/CreatePropertyBasicFields';
import { CreatePropertySpecsFields } from './create_property/CreatePropertySpecsFields';

interface CreatePropertyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newProperty: Partial<Property>) => Promise<void>;
}

export const CreatePropertyModal: React.FC<CreatePropertyModalProps> = ({ isOpen, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableOwners, setAvailableOwners] = useState<string[]>([]);
  const { filterDataSource } = useBrokerFilterOptions(null, [], []);

  const [formData, setFormData] = useState<Partial<Property>>({
    codigo: '',
    tipoinmueble: 'Apartamento',
    ciudad: 'Bogotá D.C.',
    localidad: '',
    owner: '',
    direccion: '',
    descripcion: '',
    precio_venta: 0,
    valor_administracion: 0,
    estrato: 3,
    num_habitaciones: 0,
    num_banos: 0,
    num_parqueaderos: 0,
    ascensor: 0,
    piso: '',
    status: 'Activo',
  });

  const cityOptions = useMemo(() => {
    if (!filterDataSource?.ubicaciones) return ['Bogotá D.C.', 'Medellín', 'Cali', 'Barranquilla'];
    return Object.keys(filterDataSource.ubicaciones).sort();
  }, [filterDataSource]);

  const localityOptions = useMemo(() => {
    if (!formData.ciudad || !filterDataSource?.ubicaciones?.[formData.ciudad]) return [];
    return Object.keys(filterDataSource.ubicaciones[formData.ciudad]).sort();
  }, [formData.ciudad, filterDataSource]);

  const neighborhoodOptions = useMemo(() => {
    if (!formData.ciudad || !formData.localidad || !filterDataSource?.ubicaciones?.[formData.ciudad]?.[formData.localidad]) return [];
    return filterDataSource.ubicaciones[formData.ciudad][formData.localidad].sort();
  }, [formData.ciudad, formData.localidad, filterDataSource]);

  useEffect(() => {
    if (isOpen) {
      fetchInmobiliariasConfigApi().then((data) => {
        const ownersSet = new Set<string>();
        Object.values(data).forEach((d: any) => {
          if (d && typeof d === 'object') {
            Object.keys(d).forEach((k) => k !== 'id' && typeof d[k] === 'object' && ownersSet.add(k));
          }
        });
        setAvailableOwners(Array.from(ownersSet).sort());
      }).catch(console.error);
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    let processedValue: any = value;
    if (type === 'number') processedValue = value === '' ? 0 : parseFloat(value);
    if (type === 'checkbox') processedValue = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({ ...prev, [name]: processedValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.codigo?.trim() || !formData.owner?.trim()) {
      setError('El código y el propietario son requeridos.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Error al crear el inmueble.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-[200] flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden animate-fade-in-up">
        <header className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
          <div className="flex items-center gap-2 text-sky-600">
            <PlusIcon className="w-5 h-5" />
            <h3 className="text-base font-black text-gray-800 uppercase">Crear Inmueble Manual</h3>
          </div>
          <button onClick={onClose} disabled={loading} className="text-gray-400 hover:text-gray-700">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-xs font-bold">{error}</div>}
          <CreatePropertyBasicFields
            formData={formData}
            onChange={handleChange}
            availableOwners={availableOwners}
            cityOptions={cityOptions}
            localityOptions={localityOptions}
            neighborhoodOptions={neighborhoodOptions}
          />
          <CreatePropertySpecsFields
            formData={formData}
            onChange={handleChange}
            onTagsChange={(tags) => setFormData((prev) => ({ ...prev, tag: tags, tags: tags }))}
          />

          <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
            <button type="button" onClick={onClose} disabled={loading} className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-lg">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="px-6 py-2 bg-[#0073b7] text-white rounded-lg text-xs font-black uppercase shadow-md flex items-center gap-2">
              {loading ? <SpinnerIcon className="w-4 h-4 animate-spin" /> : <SaveIcon className="w-4 h-4" />}
              {loading ? 'Creando...' : 'Crear Inmueble'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
